import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Users, CheckSquare, Clock, AlertTriangle, Calendar, TrendingUp, ChevronRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attDate, setAttDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/team-dashboard`, { withCredentials: true });
      setDashboard(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  const fetchExceptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/exceptions`, { withCredentials: true });
      setExceptions(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchDashboard(); fetchExceptions(); }, [fetchDashboard, fetchExceptions]);
  const fetchTeamAttendance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/manager/team-attendance?attendance_date=${attDate}`, { withCredentials: true });
      setTeamAttendance(data);
    } catch (e) { console.error(e); }
  }, [attDate]);

  useEffect(() => { fetchTeamAttendance(); }, [attDate, fetchTeamAttendance]);




  const approvalItems = dashboard?.pending_approvals || {};
  const attToday = dashboard?.attendance_today || {};

  const attColor = (s) => ({ present: 'text-emerald-600 bg-emerald-50', absent: 'text-red-600 bg-red-50', leave: 'text-blue-600 bg-blue-50', half_day: 'text-amber-600 bg-amber-50', weekly_off: 'text-slate-500 bg-slate-50' }[s] || 'text-slate-500 bg-slate-50');

  return (
    <MainLayout>
      <div data-testid="manager-dashboard-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Manager Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Team overview, attendance, and pending approvals</p>
          </div>
          <button onClick={() => { fetchDashboard(); fetchTeamAttendance(); fetchExceptions(); }} className="flex items-center space-x-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-50"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center space-x-2 mb-2"><Users className="w-5 h-5 text-[#0052FF]" /><p className="text-xs text-slate-500">Team Size</p></div>
            <p className="text-2xl font-bold text-slate-900">{dashboard?.team_size || 0}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
            <div className="flex items-center space-x-2 mb-2"><CheckSquare className="w-5 h-5 text-emerald-600" /><p className="text-xs text-emerald-600">Present Today</p></div>
            <p className="text-2xl font-bold text-emerald-700">{attToday.present || 0}</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <div className="flex items-center space-x-2 mb-2"><AlertTriangle className="w-5 h-5 text-red-600" /><p className="text-xs text-red-600">Absent / Leave</p></div>
            <p className="text-2xl font-bold text-red-700">{(attToday.absent || 0) + (attToday.on_leave || 0)}</p>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 cursor-pointer hover:shadow-sm" onClick={() => navigate('/manager/approvals')}>
            <div className="flex items-center space-x-2 mb-2"><Clock className="w-5 h-5 text-amber-600" /><p className="text-xs text-amber-600">Pending Approvals</p></div>
            <p className="text-2xl font-bold text-amber-700">{approvalItems.total || 0}</p>
          </div>
        </div>

        {/* Pending Approvals Breakdown */}
        {approvalItems.total > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800">Pending Approvals</h3>
              <button data-testid="go-to-approvals" onClick={() => navigate('/manager/approvals')} className="text-xs text-[#0052FF] hover:underline flex items-center space-x-1"><span>View All</span><ChevronRight className="w-3 h-3" /></button>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[
                { key: 'leave', label: 'Leave', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                { key: 'regularization', label: 'Regularization', color: 'bg-blue-50 text-blue-700 border-blue-200' },
                { key: 'ot', label: 'OT', color: 'bg-purple-50 text-purple-700 border-purple-200' },
                { key: 'reimbursement', label: 'Reimbursement', color: 'bg-orange-50 text-orange-700 border-orange-200' },
                { key: 'shift_request', label: 'Shift', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
                { key: 'compoff', label: 'Comp-Off', color: 'bg-pink-50 text-pink-700 border-pink-200' },
              ].map(({ key, label, color }) => (
                <div key={key} className={`rounded-lg border p-3 text-center ${color}`}>
                  <p className="text-lg font-bold">{approvalItems[key] || 0}</p>
                  <p className="text-xs">{label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Attendance */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Team Attendance</h3>
            <input type="date" data-testid="att-date-input" value={attDate} onChange={(e) => setAttDate(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {teamAttendance.length > 0 ? teamAttendance.map((a, i) => (
              <div key={i} data-testid={`team-att-${i}`} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                <div><p className="text-sm font-medium text-slate-800">{a.employee_name || a.employee_id}</p></div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${attColor(a.status)}`}>{a.status || 'not marked'}</span>
                  {a.check_in && <span className="text-xs text-slate-500">{a.check_in}</span>}
                </div>
              </div>
            )) : <p className="text-sm text-slate-400 py-6 text-center">No attendance records for this date</p>}
          </div>
        </div>

        {/* Exceptions */}
        {exceptions.length > 0 && (
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            <div className="p-4 border-b border-red-100 bg-red-50"><h3 className="text-sm font-semibold text-red-800 flex items-center space-x-2"><AlertTriangle className="w-4 h-4" /><span>Unresolved Exceptions ({exceptions.length})</span></h3></div>
            <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {exceptions.map((e, i) => (
                <div key={i} className="px-4 py-3">
                  <p className="text-sm text-slate-800">{e.exception_type}: {e.message || e.description}</p>
                  <p className="text-xs text-slate-500">{e.employee_id} &middot; {e.period}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ManagerDashboard;
